import { useEffect, useMemo, useRef, useState } from 'react';
// import { type PromoCodeDto, type PromoCodeType } from '@/services/promo-codes.service';
import { type PromoCodeType } from '@/services/promo-codes.service';
import { inventoryApi } from '@/services/inventory-api.service';
import filterStyles from '@/components/AdminCheckboxFilter/AdminCheckboxFilter.module.css';
import sharedFilterStyles from '@/components/AdminFilterShared/AdminFilterShared.module.css';
import styles from './AdminPromoCodesSection.module.css';

export type PromoFormState = {
  type: PromoCodeType;
  value: string;
  code: string;
  amount: string;
  oneTime: boolean;
  minOrderAmount: string;
  startDate: string;
  endDate: string;
  domains: DomainOption[];
  active: boolean;
};

type DomainOption = {
  key: string;
  label: string;
};

let cachedDomainOptions: DomainOption[] | null = null;
let cachedDomainOptionsRequest: Promise<DomainOption[]> | null = null;

const mapDomainOptions = (entries: Array<{ id: number; value: string }>): DomainOption[] => {
  return entries
    .filter((entry) => Boolean(entry?.id && entry?.value))
    .map((entry) => ({ key: String(entry.id), label: entry.value }));
};

const loadCachedDomainOptions = async (): Promise<DomainOption[]> => {
  if (cachedDomainOptions) {
    return cachedDomainOptions;
  }

  if (!cachedDomainOptionsRequest) {
    cachedDomainOptionsRequest = inventoryApi
      .getDomains()
      .then((entries) => {
        const mappedOptions = Array.isArray(entries) ? mapDomainOptions(entries) : [];
        cachedDomainOptions = mappedOptions;
        return mappedOptions;
      })
      .finally(() => {
        cachedDomainOptionsRequest = null;
      });
  }

  return cachedDomainOptionsRequest;
};

type AdminPromoCodeModalProps = {
  showCreateModal: boolean;
  showBulkModal: boolean;
  form: PromoFormState;
  submitError: string | null;
  onClose: () => void;
  onOpenCreateSingle: () => void;
  onOpenCreateBulk: () => void;
  onInputChange: (patch: Partial<PromoFormState>) => void;
  onCreateSingle: () => void;
  onCreateBulk: () => void;
};

export const AdminPromoCodeModal = ({
  showCreateModal,
  showBulkModal,
  form,
  submitError,
  onClose,
  // onOpenCreateSingle,
  // onOpenCreateBulk,
  onInputChange,
  onCreateSingle,
  onCreateBulk,
}: AdminPromoCodeModalProps) => {
  if (!showCreateModal && !showBulkModal) {
    return null;
  }

  const [isValidityEnabled, setIsValidityEnabled] = useState(false);
  const [isMinAmountEnabled, setIsMinAmountEnabled] = useState(false);
  const [isDomainEnabled, setIsDomainEnabled] = useState(false);
  const [isDomainDropdownOpen, setIsDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState('');
  const [domainOptions, setDomainOptions] = useState<DomainOption[]>([]);
  const [isDomainLoading, setIsDomainLoading] = useState(false);
  const domainDropdownRef = useRef<HTMLDivElement | null>(null);

  const isVisible = showCreateModal || showBulkModal;

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    setIsValidityEnabled(Boolean(form.startDate || form.endDate));
    setIsMinAmountEnabled(Boolean(form.minOrderAmount));
    setIsDomainEnabled(Boolean(form.domains.length));
    setIsDomainDropdownOpen(false);
    setDomainSearchQuery('');
  }, [isVisible]);

  useEffect(() => {
    if (!isDomainDropdownOpen) {
      return;
    }

    const onOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (domainDropdownRef.current?.contains(target)) {
        return;
      }

      setIsDomainDropdownOpen(false);
    };

    document.addEventListener('mousedown', onOutsideClick);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
    };
  }, [isDomainDropdownOpen]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    let isCancelled = false;
    if (cachedDomainOptions) {
      setDomainOptions(cachedDomainOptions);
      setIsDomainLoading(false);
      return;
    }

    setIsDomainLoading(true);
    void loadCachedDomainOptions()
      .then((options) => {
        if (isCancelled) {
          return;
        }

        setDomainOptions(options);
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setDomainOptions([]);
      })
      .finally(() => {
        if (!isCancelled) {
          setIsDomainLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [isVisible]);

  const mergedDomainOptions = useMemo(() => {
    const result: DomainOption[] = [];
    const seen = new Set<string>();

    domainOptions.forEach((option) => {
      if (!option.key || seen.has(option.key)) {
        return;
      }
      seen.add(option.key);
      result.push(option);
    });

    form.domains.forEach(domain => {
      const selectedId = domain.key;
      if (!selectedId || seen.has(selectedId)) {
      return;
      }
      seen.add(selectedId);
      result.push({ key: selectedId, label: selectedId });
    });

    return result;
  }, [domainOptions, form.domains]);

  const filteredDomainOptions = useMemo(() => {
    const query = domainSearchQuery.trim().toLowerCase();
    if (!query) {
      return mergedDomainOptions;
    }

    return mergedDomainOptions.filter((option) => option.label.toLowerCase().includes(query));
  }, [domainSearchQuery, mergedDomainOptions]);

  const selectedDomainSummary = useMemo(() => {
    if (!form.domains.length) {
      return 'Select domain';
    }

    return `${String(form.domains.length)} selected`;
  }, [form.domains.length]);

  const toggleValidity = () => {
    const nextEnabled = !isValidityEnabled;
    setIsValidityEnabled(nextEnabled);
    if (!nextEnabled) {
      onInputChange({ startDate: '', endDate: '' });
    }
  };

  const toggleMinOrderAmount = () => {
    const nextEnabled = !isMinAmountEnabled;
    setIsMinAmountEnabled(nextEnabled);
    if (!nextEnabled) {
      onInputChange({ minOrderAmount: '' });
    }
  };

  const toggleDomainRestriction = () => {
    const nextEnabled = !isDomainEnabled;
    setIsDomainEnabled(nextEnabled);
    if (!nextEnabled) {
      onInputChange({ domains: [] });
      setIsDomainDropdownOpen(false);
      setDomainSearchQuery('');
    }
  };

  const preventNumberInputScroll = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };

  const formDomainIds = form.domains.map(d => d.key);

  const toggleDomain = (domain: DomainOption) => {
    const domainId = domain.key;
    const hasDomain = formDomainIds.includes(domainId);
    const nextDomains = hasDomain
      ? form.domains.filter(d => d.key !== domainId)
      : [...form.domains, domain];
    onInputChange({ domains: nextDomains });
  };

  const title = showBulkModal
      ? 'Bulk generation'
      : 'Create new promo';
  const actionLabel = showBulkModal ? 'Generate' : 'Create';

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3>{title}</h3>
          <button type="button" className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        {submitError ? <div className={styles.error}>{submitError}</div> : null}

        {/*<div className={styles.modeSwitch}>
          <button
              type="button"
              className={`${styles.modeButton} ${showCreateModal ? styles.modeButtonActive : ''}`}
              onClick={onOpenCreateSingle}
          >
              Single code
          </button>
          <button
              type="button"
              className={`${styles.modeButton} ${showBulkModal ? styles.modeButtonActive : ''}`}
              onClick={onOpenCreateBulk}
          >
              Bulk generation
          </button>
        </div>
        */}

        <div className={styles.modalBody}>
          <section className={styles.primaryFields}>
            <p className={styles.sectionLabel}>Type</p>
            <div className={styles.typeCards}>
              <button
                type="button"
                className={`${styles.typeCard} ${form.oneTime ? styles.typeCardActive : ''}`}
                onClick={() => onInputChange({ oneTime: true })}
              >
                <span className={styles.typeCardTitle}>One-time</span>
                <span className={styles.typeCardText}>Can be redeemed once after a successful payment.</span>
                <span className={styles.typeCardCheck}>{form.oneTime ? '✓' : ''}</span>
              </button>

              <button
                type="button"
                className={`${styles.typeCard} ${!form.oneTime ? styles.typeCardActive : ''}`}
                onClick={() => onInputChange({ oneTime: false })}
              >
                <span className={styles.typeCardTitle}>Multiple-time</span>
                <span className={styles.typeCardText}>Can be used multiple times by different customers.</span>
                <span className={styles.typeCardCheck}>{!form.oneTime ? '✓' : ''}</span>
              </button>
            </div>

            <label className={styles.fieldLabel}>
              <span className={styles.requiredLabel}>Value</span>
              <div className={styles.valueWrap}>
                <input
                  type="number"
                  value={form.value}
                  onChange={(event) => {
                    onInputChange({ value: event.target.value });
                  }}
                  onWheel={preventNumberInputScroll}
                  className={`${styles.input} ${styles.valueInput}`}
                />
                <div className={styles.valueSuffix}>
                  <button
                    type="button"
                    className={`${styles.unitChip} ${form.type === 'fixed' ? styles.unitChipActive : ''}`}
                    onClick={() => {
                      onInputChange({ type: 'fixed' });
                    }}
                  >
                    EUR
                  </button>
                  <button
                    type="button"
                    className={`${styles.unitChip} ${form.type === 'percentage' ? styles.unitChipActive : ''}`}
                    onClick={() => {
                      onInputChange({ type: 'percentage' });
                    }}
                  >
                    %
                  </button>
                </div>
              </div>
            </label>

            {(showCreateModal) ? (
              <label className={styles.fieldLabel}>
                <span className={styles.requiredLabel}>Code</span>
                <input
                  type="text"
                  value={form.code}
                  onChange={(event) => {
                    onInputChange({ code: event.target.value.toUpperCase() });
                  }}
                  className={styles.input}
                  placeholder="e.g. SKIPASSNEW"
                />
              </label>
            ) : null}

            {showBulkModal ? (
              <label className={styles.fieldLabel}>
                <span className={styles.requiredLabel}>Amount of codes</span>
                <input
                  type="number"
                  min={1}
                  value={form.amount}
                  onChange={(event) => {
                    onInputChange({ amount: event.target.value });
                  }}
                  onWheel={preventNumberInputScroll}
                  className={styles.input}
                />
              </label>
            ) : null}
          </section>

          <section className={styles.restrictionsBlock}>
            <p className={styles.sectionLabel}>Restrictions</p>
            <div className={styles.restrictionRows}>
              <div className={styles.restrictionRow}>
                <div className={styles.restrictionTitleCol}>
                  <button
                    type="button"
                    className={styles.restrictionSwitchButton}
                    onClick={toggleValidity}
                    aria-pressed={isValidityEnabled}
                  >
                    <span className={`${styles.statusSwitch} ${isValidityEnabled ? styles.statusSwitchOn : ''}`}>
                      <span className={styles.statusThumb}>{isValidityEnabled ? '✓' : ''}</span>
                    </span>
                  </button>
                  <span className={styles.restrictionName}>Validity period</span>
                </div>

                <div className={styles.restrictionControls}>
                  <div className={styles.validityGrid}>
                    <label className={styles.compactFieldLabel}>
                      <span>From</span>
                      <input
                        type="date"
                        value={form.startDate}
                        onChange={(event) => {
                          onInputChange({ startDate: event.target.value });
                        }}
                        className={styles.input}
                        disabled={!isValidityEnabled}
                      />
                    </label>

                    <label className={styles.compactFieldLabel}>
                      <span>To</span>
                      <input
                        type="date"
                        value={form.endDate}
                        onChange={(event) => {
                          onInputChange({ endDate: event.target.value });
                        }}
                        className={styles.input}
                        disabled={!isValidityEnabled}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className={styles.restrictionRow}>
                <div className={styles.restrictionTitleCol}>
                  <button
                    type="button"
                    className={styles.restrictionSwitchButton}
                    onClick={toggleMinOrderAmount}
                    aria-pressed={isMinAmountEnabled}
                  >
                    <span className={`${styles.statusSwitch} ${isMinAmountEnabled ? styles.statusSwitchOn : ''}`}>
                      <span className={styles.statusThumb}>{isMinAmountEnabled ? '✓' : ''}</span>
                    </span>
                  </button>
                  <span className={styles.restrictionName}>Min order amount</span>
                </div>

                <div className={styles.restrictionControls}>
                  <div className={styles.valueWrap}>
                    <input
                      type="number"
                      min={0}
                      value={form.minOrderAmount}
                      onChange={(event) => {
                        onInputChange({ minOrderAmount: event.target.value });
                      }}
                      onWheel={preventNumberInputScroll}
                      className={`${styles.input} ${styles.valueInput}`}
                      placeholder="Optional"
                      disabled={!isMinAmountEnabled}
                    />
                    <div className={styles.valueSuffix}>
                      <span className={styles.staticSuffix}>EUR</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.restrictionRow}>
                <div className={styles.restrictionTitleCol}>
                  <button
                    type="button"
                    className={styles.restrictionSwitchButton}
                    onClick={toggleDomainRestriction}
                    aria-pressed={isDomainEnabled}
                  >
                    <span className={`${styles.statusSwitch} ${isDomainEnabled ? styles.statusSwitchOn : ''}`}>
                      <span className={styles.statusThumb}>{isDomainEnabled ? '✓' : ''}</span>
                    </span>
                  </button>
                  <span className={styles.restrictionName}>Domain</span>
                </div>

                <div className={styles.restrictionControls}>
                  <div className={styles.domainSelect} ref={domainDropdownRef}>
                    <button
                      type="button"
                      className={styles.domainSelectTrigger}
                      onClick={() => {
                        if (!isDomainEnabled) {
                          return;
                        }
                        setIsDomainDropdownOpen((prev) => !prev);
                      }}
                      disabled={!isDomainEnabled}
                    >
                      <span className={form.domains.length ? styles.domainSelectedText : styles.domainPlaceholder}>
                        {selectedDomainSummary}
                      </span>
                      <span className={styles.domainChevron}>⌄</span>
                    </button>

                    {isDomainDropdownOpen ? (
                      <div className={styles.domainDropdown}>
                        <div className={filterStyles.controlsRow}>
                          <label className={filterStyles.searchBox}>
                            <span className={filterStyles.searchIcon}>⌕</span>
                            <input
                              type="text"
                              value={domainSearchQuery}
                              onChange={(event) => {
                                setDomainSearchQuery(event.target.value);
                              }}
                              className={filterStyles.searchInput}
                              placeholder="Search..."
                            />
                          </label>
                          <button
                            type="button"
                            className={`${sharedFilterStyles.reset} ${form.domains.length ? sharedFilterStyles.resetActive : ''}`}
                            onClick={() => {
                              onInputChange({ domains: [] });
                            }}
                          >
                            Reset All
                          </button>
                        </div>

                        {isDomainLoading ? <div className={filterStyles.hint}>Loading...</div> : null}

                        <div className={`${filterStyles.options} ${filterStyles.optionsGrid}`}>
                          {filteredDomainOptions.map((option) => (
                            <label key={option.key} className={filterStyles.optionLabel}>
                              <input
                                type="checkbox"
                                checked={formDomainIds.includes(option.key)}
                                onChange={() => {
                                  toggleDomain(option);
                                }}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                          {!isDomainLoading && filteredDomainOptions.length === 0 ? (
                            <div className={filterStyles.hint}>No matching domains</div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className={styles.modalFooterSplit}>
          <label className={styles.statusRow}>
            <span className={styles.statusLabel}>Status</span>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => {
                onInputChange({ active: event.target.checked });
              }}
              className={styles.statusCheckbox}
            />
            <span className={`${styles.statusSwitch} ${form.active ? styles.statusSwitchOn : ''}`}>
              <span className={styles.statusThumb}>{form.active ? '✓' : ''}</span>
            </span>
            <span className={styles.statusValue}>{form.active ? 'Active' : 'Inactive'}</span>
          </label>

          <div className={styles.footerActions}>
            <button type="button" className={styles.secondaryButton} onClick={onClose}>
              Cancel
            </button>
            {showBulkModal ? (
              <button type="button" className={styles.primaryButton} onClick={() => void onCreateBulk()}>
                {actionLabel}
              </button>
            ) : (
              <button type="button" className={styles.primaryButton} onClick={() => void onCreateSingle()}>
                {actionLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
